import {
  Alert,
  Button,
  Container,
  Heading,
  Spinner,
  Stack,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"

import { AttemptsService } from "@/client"
import { QuestionReview } from "@/components/TestTaking/QuestionReview"
import { readAttemptTimerRecord } from "@/components/TestTaking/StudentTimer/studentTimer"

export const Route = createFileRoute(
  "/_layout/quizzes/$quizId/attempt/$attemptId/question/$questionIndex/review",
)({
  component: QuestionReviewPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      questionId: (search.questionId as string) || "",
      totalQuestions: search.totalQuestions
        ? Number.parseInt(search.totalQuestions as string, 10)
        : undefined,
    }
  },
})

function QuestionReviewPage() {
  const { quizId, attemptId, questionIndex } = Route.useParams()
  const { questionId, totalQuestions } = Route.useSearch()
  const navigate = useNavigate()

  const { data, isLoading, error } = useQuery({
    queryKey: ["question-review", quizId, attemptId, questionId],
    queryFn: () =>
      AttemptsService.getQuestionReview({
        quizId,
        attemptId,
        questionId,
      }),
    retry: false,
  })

  const currentIndex = Number.parseInt(questionIndex, 10)
  const normalizedIndex = Number.isNaN(currentIndex) ? 0 : currentIndex
  const isLastQuestion = totalQuestions
    ? normalizedIndex >= totalQuestions - 1
    : false

  const handleNext = () => {
    if (isLastQuestion) {
      navigate({
        to: "/quizzes/$quizId/attempt/$attemptId/question/$questionIndex",
        params: {
          quizId,
          attemptId,
          questionIndex,
        },
      })
      return
    }

    const nextIndex = normalizedIndex + 1
    navigate({
      to: "/quizzes/$quizId/attempt/$attemptId/question/$questionIndex",
      params: {
        quizId,
        attemptId,
        questionIndex: nextIndex.toString(),
      },
    })
  }

  if (isLoading) {
    return (
      <Container maxW="4xl" py={10}>
        <Stack align="center">
          <Spinner size="xl" />
          <Heading size="md">Loading question review...</Heading>
        </Stack>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxW="4xl" py={10}>
        <Alert.Root status="error">
          <Alert.Title>Error loading question review</Alert.Title>
          <Alert.Description>
            {(error as Error).message || "Please try again later."}
          </Alert.Description>
        </Alert.Root>
      </Container>
    )
  }

  if (!data) {
    return null
  }

  return (
    <Container maxW="4xl" py={10}>
      <Stack gap={6}>
        <Heading size="xl">Question Review</Heading>

        <QuestionReview
          question={data}
          quizId={quizId}
          paceOvertime={
            readAttemptTimerRecord(attemptId)?.questionOvertime[
              data.question_id
            ]
          }
        />

        <Button
          onClick={handleNext}
          colorPalette={isLastQuestion ? "green" : "blue"}
          alignSelf="flex-end"
        >
          {isLastQuestion ? "Continue to Submit" : "Next Question"}
        </Button>
      </Stack>
    </Container>
  )
}
